import { lazy as reactLazy, Suspense } from "react";

// Auto-reload on stale chunk (after a new deploy the old hashed file is gone)
const lazy: typeof reactLazy = (factory) =>
  reactLazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message ?? err);
      if (/Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg)) {
        const key = "__lov_chunk_reload__";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise(() => {}) as never;
        }
      }
      throw err;
    }
  });
// Toasts werden erst bei Bedarf nachgeladen – hält das Start-Bundle klein
const Toaster = reactLazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = reactLazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LocaleProvider } from "@/contexts/LocaleContext";
import Layout from "@/components/layout/Layout";
import Index from "./pages/Index";
import {
  LEGACY_REDIRECTS,
  LEGACY_EN_REDIRECTS,
  Redirect,
  AnfrageLegacyRedirect,
} from "./components/LegacyRedirects";

// Öffentliche Seiten: per Route lazy-laden, damit der Startseiten-Bundle klein bleibt
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const SuitesOverview = lazy(() => import("./pages/SuitesOverview"));
const SuiteDetail = lazy(() => import("./pages/SuiteDetail"));
const ErlebnisseOverview = lazy(() => import("./pages/ErlebnisseOverview"));
const Bewertungen = lazy(() => import("./pages/Bewertungen"));
const ErlebnisseSommer = lazy(() => import("./pages/ErlebnisseSommer"));
const ErlebnisseWinter = lazy(() => import("./pages/ErlebnisseWinter"));
const TopEvents = lazy(() => import("./pages/TopEvents"));
const LerchPlus = lazy(() => import("./pages/LerchPlus"));
const Wellness = lazy(() => import("./pages/Wellness"));
const LageAnreise = lazy(() => import("./pages/LageAnreise"));
const Buchen = lazy(() => import("./pages/Buchen"));
const Anfrage = lazy(() => import("./pages/Anfrage"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Backend-Bereiche: komplett lazy – werden für normale Besucher nie geladen
const HwmBackend = lazy(() => import("./pages/HwmBackend"));
const HwmLayout = lazy(() => import("./pages/hwm/HwmLayout"));
const HwmDashboard = lazy(() => import("./pages/hwm/HwmDashboard"));
const HwmSetup = lazy(() => import("./pages/hwm/HwmSetup"));
const HwmPlugins = lazy(() => import("./pages/hwm/HwmPlugins"));
const CmsAuth = lazy(() => import("./pages/cms/CmsAuth"));
const CmsLayout = lazy(() => import("./pages/cms/CmsLayout"));
const CmsDashboard = lazy(() => import("./pages/cms/CmsDashboard"));
const CmsSections = lazy(() => import("./pages/cms/CmsSections"));
const CmsPageEdit = lazy(() => import("./pages/cms/CmsPageEdit"));
const CmsUsers = lazy(() => import("./pages/cms/CmsUsers"));
const CmsSeo = lazy(() => import("./pages/cms/CmsSeo"));
const CmsTranslations = lazy(() => import("./pages/cms/CmsTranslations"));
const CmsSetupAccount = lazy(() => import("./pages/cms/CmsSetupAccount"));
const CmsSeasonSettings = lazy(() => import("./pages/cms/CmsSeasonSettings"));
const CmsSocialMedia = lazy(() => import("./pages/cms/CmsSocialMedia"));
const CmsReviews = lazy(() => import("./pages/cms/CmsReviews"));
const CmsPopups = lazy(() => import("./pages/cms/CmsPopups"));
const CmsMedia = lazy(() => import("./pages/cms/CmsMedia"));
const CmsConnections = lazy(() => import("./pages/cms/CmsConnections"));
const CmsVerify = lazy(() => import("./pages/cms/CmsVerify"));
const CmsResetPassword = lazy(() => import("./pages/cms/CmsResetPassword"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[40vh]" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <LocaleProvider>
        <>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Verstecktes Backend – Login-Guard + Sidebar-Layout */}
                <Route path="/hwm-backend" element={<HwmBackend />}>
                  <Route element={<HwmLayout />}>
                    <Route index element={<HwmDashboard />} />
                    <Route path="setup" element={<HwmSetup />} />
                    <Route path="plugins" element={<HwmPlugins />} />
                  </Route>
                </Route>

                {/* CMS Backend – eigenes geschütztes System */}
                <Route path="/cms-backend/login" element={<CmsAuth />} />
                <Route path="/cms-backend/verify" element={<CmsVerify />} />
                <Route path="/cms-backend/reset-password" element={<CmsResetPassword />} />
                <Route path="/cms-backend/setup-account" element={<CmsSetupAccount />} />
                <Route path="/cms-backend" element={<CmsLayout />}>
                  <Route path="dashboard" element={<CmsDashboard />} />
                  <Route path="sections" element={<CmsSections />} />
                  <Route path="sections/:pageId" element={<CmsPageEdit />} />
                  <Route path="seo" element={<CmsSeo />} />
                  <Route path="translations" element={<CmsTranslations />} />
                  <Route path="users" element={<CmsUsers />} />
                  <Route path="season" element={<CmsSeasonSettings />} />
                  <Route path="social" element={<CmsSocialMedia />} />
                  <Route path="reviews" element={<CmsReviews />} />
                  <Route path="popups" element={<CmsPopups />} />
                  <Route path="media" element={<CmsMedia />} />
                  <Route path="connections" element={<CmsConnections />} />
                </Route>

                {/* Öffentliche Seiten mit Layout */}
                <Route element={<Layout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/suites" element={<SuitesOverview />} />
                  <Route path="/suites/:slug" element={<SuiteDetail />} />
                  <Route path="/lerch-plus" element={<LerchPlus />} />
                  <Route path="/wellness" element={<Wellness />} />
                  <Route path="/erlebnisse" element={<ErlebnisseOverview />} />
                  <Route path="/bewertungen" element={<Bewertungen />} />
                  <Route path="/erlebnisse/sommer" element={<ErlebnisseSommer />} />
                  <Route path="/erlebnisse/winter" element={<ErlebnisseWinter />} />
                  <Route path="/erlebnisse/top-events" element={<TopEvents />} />
                  <Route path="/buchen" element={<Buchen />} />
                  <Route path="/anfrage" element={<Anfrage />} />
                  <Route path="/lage-anreise" element={<LageAnreise />} />
                  <Route path="/sitemap" element={<SitemapPage />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />

                  {/* Legacy WordPress URL redirects (alte aparthotel-lerch.at) */}
                  <Route path="/anfragen" element={<AnfrageLegacyRedirect />} />
                  <Route path="/en/request" element={<AnfrageLegacyRedirect lang="en" />} />
                  {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                    <Route key={from} path={from} element={<Redirect to={to} />} />
                  ))}
                  {Object.entries(LEGACY_EN_REDIRECTS).map(([from, to]) => (
                    <Route key={from} path={from} element={<Redirect to={to} lang="en" />} />
                  ))}

                  {/* Catch-all für dynamische WordPress-Seiten */}
                  <Route path="/:slug" element={<DynamicPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </>
      </LocaleProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
